/**
 * Printable purchase-order document (backlog item #16, print half).
 *
 * Most of the clinic's suppliers have no email on file — POs are sent by
 * WhatsApp — so a downloadable/printable PO is the critical path. This module
 * builds a self-contained A4 HTML document for a PO and opens it in a new
 * window where the browser's print dialog doubles as "Save as PDF".
 *
 * The new-window + document.write approach is intentional: it sidesteps any
 * interference from the app's own stylesheets and only needs a tiny
 * @media print sheet of its own (same pattern as a print-friendly invoice).
 *
 * Layout mirrors the clinic's real PO history Excel: SKU/Code, Description,
 * UOM, Qty, Unit Price (ex-GST), Unit Price (incl. GST) and GST-inclusive
 * totals (Singapore GST 9%).
 */

export const SG_GST_RATE = 0.09;

const CLINIC_NAME = 'Twin City Endodontics';
const CLINIC_ADDRESS =
  '583 Orchard Road #06-04 Forum The Shopping Mall, Singapore 238884';

export interface PurchaseOrderPrintLine {
  /** Catalog code (inventory_items.given_id) — printed as "—" when absent. */
  itemCode: string | null;
  description: string;
  uom: string | null;
  qty: number;
  /** Unit price excluding GST. Null = price not captured on the PO line. */
  unitPrice: number | null;
}

export interface PurchaseOrderPrintPayload {
  poNumber: string;
  /** ISO timestamp of when the PO was created/ordered. */
  orderDate: string;
  /** Optional ISO date — only printed when present. */
  expectedDeliveryDate?: string | null;
  supplierName: string;
  supplierContactPerson?: string | null;
  supplierPhone?: string | null;
  supplierEmail?: string | null;
  notes?: string | null;
  lines: PurchaseOrderPrintLine[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * All money math is done in integer cents so the printed line totals always
 * add up to the printed subtotal/GST/total exactly (raw float sums could
 * disagree with the rounded line totals by a cent or two).
 */
const toCents = (qty: number, unitPrice: number): number =>
  Math.round(qty * unitPrice * 100);

/** Unit price incl. GST = ex-GST cents × 1.09, rounded to the nearest cent. */
const unitInclGstCents = (unitExGstCents: number): number =>
  Math.round(unitExGstCents * (1 + SG_GST_RATE));

const formatCents = (cents: number): string =>
  (cents / 100).toLocaleString('en-SG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('en-SG', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const buildPurchaseOrderPrintHtml = (
  payload: PurchaseOrderPrintPayload
): string => {
  const subtotalCents = payload.lines.reduce(
    (sum, line) => sum + (line.unitPrice != null ? toCents(line.qty, line.unitPrice) : 0),
    0
  );
  const gstCents = Math.round(subtotalCents * SG_GST_RATE);
  const totalCents = subtotalCents + gstCents;

  const lineRows = payload.lines
    .map((line, index) => {
      const unitExCents = line.unitPrice != null ? Math.round(line.unitPrice * 100) : null;
      const lineTotalCents = line.unitPrice != null ? toCents(line.qty, line.unitPrice) : null;
      return `
        <tr>
          <td class="num">${index + 1}</td>
          <td class="code">${escapeHtml(line.itemCode || '—')}</td>
          <td>${escapeHtml(line.description)}</td>
          <td>${escapeHtml(line.uom || '—')}</td>
          <td class="num">${line.qty}</td>
          <td class="num">${unitExCents != null ? formatCents(unitExCents) : '—'}</td>
          <td class="num">${unitExCents != null ? formatCents(unitInclGstCents(unitExCents)) : '—'}</td>
          <td class="num">${lineTotalCents != null ? formatCents(lineTotalCents) : '—'}</td>
        </tr>`;
    })
    .join('');

  const supplierContactRows = [
    payload.supplierContactPerson
      ? `<p>Attn: ${escapeHtml(payload.supplierContactPerson)}</p>`
      : '',
    payload.supplierPhone ? `<p>Phone: ${escapeHtml(payload.supplierPhone)}</p>` : '',
    payload.supplierEmail ? `<p>Email: ${escapeHtml(payload.supplierEmail)}</p>` : '',
  ].join('');

  const notesBlock = payload.notes?.trim()
    ? `
      <div class="notes">
        <h3>Notes</h3>
        <p>${escapeHtml(payload.notes.trim())}</p>
      </div>`
    : '';

  const expectedDeliveryRow = payload.expectedDeliveryDate
    ? `<p><strong>Expected delivery:</strong> ${escapeHtml(formatDate(payload.expectedDeliveryDate))}</p>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(payload.poNumber)} — Purchase Order</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #000;
    background: #fff;
    margin: 0;
    padding: 24px;
    font-size: 13px;
    line-height: 1.45;
  }
  @media print {
    body { padding: 0; }
    .no-print { display: none !important; }
  }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #000; padding-bottom: 12px; }
  header h1 { font-size: 20px; letter-spacing: 0.04em; margin: 0 0 2px; text-transform: uppercase; }
  header .doc-type { font-size: 15px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; }
  header .meta { text-align: right; font-size: 13px; }
  header .meta p { margin: 2px 0; }
  .parties { display: flex; gap: 24px; margin-top: 18px; }
  .party { flex: 1; border: 1px solid #000; padding: 10px 12px; }
  .party h3 { margin: 0 0 6px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; }
  .party p { margin: 2px 0; }
  /* 8 columns (extra incl-GST unit price) — 11px + tighter padding keeps the
     table comfortably inside the A4 content width (~182mm at 14mm margins). */
  table.lines { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 11px; }
  table.lines th, table.lines td { border: 1px solid #000; padding: 6px 6px; vertical-align: top; }
  table.lines th { background: #f0f0f0; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; text-align: left; }
  table.lines td.num, table.lines th.num { text-align: right; white-space: nowrap; }
  table.lines td.code { white-space: nowrap; }
  table.totals { margin-top: 0; margin-left: auto; border-collapse: collapse; min-width: 280px; }
  table.totals td { padding: 5px 8px; border: 1px solid #000; }
  table.totals td:first-child { font-weight: 600; }
  table.totals td:last-child { text-align: right; white-space: nowrap; }
  table.totals tr.grand td { font-weight: 700; background: #f0f0f0; }
  .notes { margin-top: 18px; border: 1px solid #000; padding: 10px 12px; }
  .notes h3 { margin: 0 0 6px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; }
  .notes p { margin: 0; white-space: pre-wrap; }
  footer { margin-top: 28px; border-top: 1px solid #000; padding-top: 10px; font-size: 12px; }
  .print-hint { margin-top: 20px; text-align: center; color: #555; font-size: 12px; }
</style>
</head>
<body>
  <header>
    <div>
      <h1>${escapeHtml(CLINIC_NAME)}</h1>
      <p class="doc-type">Purchase Order</p>
    </div>
    <div class="meta">
      <p><strong>PO No:</strong> ${escapeHtml(payload.poNumber)}</p>
      <p><strong>Order date:</strong> ${escapeHtml(formatDate(payload.orderDate))}</p>
      ${expectedDeliveryRow}
    </div>
  </header>

  <div class="parties">
    <div class="party">
      <h3>Supplier</h3>
      <p><strong>${escapeHtml(payload.supplierName)}</strong></p>
      ${supplierContactRows}
    </div>
    <div class="party">
      <h3>Bill to / Deliver to</h3>
      <p><strong>${escapeHtml(CLINIC_NAME)}</strong></p>
      <p>${escapeHtml(CLINIC_ADDRESS)}</p>
    </div>
  </div>

  <table class="lines">
    <thead>
      <tr>
        <th class="num">#</th>
        <th>Item code</th>
        <th>Description</th>
        <th>UOM</th>
        <th class="num">Qty</th>
        <th class="num">Unit price (ex-GST)</th>
        <th class="num">Unit price (incl. GST)</th>
        <th class="num">Line total (ex-GST)</th>
      </tr>
    </thead>
    <tbody>
      ${lineRows}
    </tbody>
  </table>

  <table class="totals">
    <tr>
      <td>Subtotal (ex-GST)</td>
      <td>$${formatCents(subtotalCents)}</td>
    </tr>
    <tr>
      <td>GST 9%</td>
      <td>$${formatCents(gstCents)}</td>
    </tr>
    <tr class="grand">
      <td>Total (incl. GST)</td>
      <td>$${formatCents(totalCents)}</td>
    </tr>
  </table>

  ${notesBlock}

  <footer>
    <p>Please confirm receipt of this order.</p>
  </footer>

  <p class="print-hint no-print">
    Use your browser's print dialog to print this purchase order or save it as a PDF.
  </p>

  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 150);
    });
  </script>
</body>
</html>`;
};

/**
 * Opens the printable PO in a new window and triggers the print dialog
 * (which doubles as "Save as PDF"). Returns false when the popup was
 * blocked so the caller can surface a toast.
 */
export const openPurchaseOrderPrintView = (
  payload: PurchaseOrderPrintPayload
): boolean => {
  // NOTE: no 'noopener' here on purpose — it would make window.open return
  // null and we need the handle to document.write the PO into the popup.
  const printWindow = window.open('', '_blank', 'width=900,height=1100');
  if (!printWindow) {
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(buildPurchaseOrderPrintHtml(payload));
  printWindow.document.close();
  printWindow.focus();
  return true;
};

export default openPurchaseOrderPrintView;
